import { useMutation } from '@apollo/client/react';
import { ConfirmationDialog } from '@optools/components/dialog/confirmation-dialog';
import { Button } from '@optools/components/ui/button';
import { ROUTE_MEDIA, SET_SLATE } from '@optools/services/operator-gateway/logger/mutations';
import { LoggerGamePipeline } from '@optools/services/operator-gateway/logger/types';
import {
  MediaFeedType,
  RouteType,
  StreamState,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { selectLoggerConfig } from '@optools/store/configApiSlice';
import { useAppSelector } from '@optools/store/hooks';
import { determineGameState, GAME_STATE } from '@optools/utils/game-state';
import { hasVideoMedia } from '@optools/utils/pipeline';
import { toast } from 'sonner';

import { determineNotificationPosition } from '../utils/notification';

export interface StateButtonProps {
  buttonState: string;
  description: string;
  disabled: boolean;
  gameState: string;
  onClick: (routeType?: RouteType) => void;
}

function StateButton({ buttonState, description, disabled, gameState, onClick }: StateButtonProps) {
  const active = gameState === buttonState;
  return (
    <ConfirmationDialog description={description} title="Game State Change" onConfirm={onClick}>
      <Button className="w-50" disabled={disabled} variant={active ? 'default' : 'outline'}>
        {buttonState}
      </Button>
    </ConfirmationDialog>
  );
}

export interface GameStateControlsProps {
  debugMode?: boolean;
  mediaId: string;
  pipeline: LoggerGamePipeline;
  side: MediaFeedType;
}

export function GameStateControls({
  debugMode = false,
  mediaId,
  pipeline,
  side,
}: GameStateControlsProps) {
  const { commercialPrePostSlateCode, commercialSlateCode, staticSlateCodes } =
    useAppSelector(selectLoggerConfig);

  const gameState = determineGameState(pipeline);

  const { activeSlateCode } = pipeline;

  const [routeMedia, { loading: routeMutationLoading }] = useMutation(ROUTE_MEDIA, {
    onCompleted: ({ routeMedia }) => {
      const result = routeMedia?.[0];
      if (result?.success) {
        toast.success('Game State Updated', {
          description: 'Game state updated successfully',
          position: determineNotificationPosition(side),
        });
      } else {
        toast.error('Error Updating Game State', {
          description: result.reason ?? 'Reason: Unknown',
          duration: Infinity,
          position: determineNotificationPosition(side),
        });
      }
    },
  });

  const [setSlate, { loading: setSlateMutationLoading }] = useMutation(SET_SLATE, {
    onCompleted: ({ setSlate }) => {
      const { reason, success } = setSlate[0];

      if (success) {
        toast.success('Game State Updated', {
          description: 'Game state updated successfully',
          position: determineNotificationPosition(side),
        });
      } else {
        toast.error('Failed to Insert Slate', {
          description: reason ?? 'Reason: Unknown',
          duration: Infinity,
          position: determineNotificationPosition(side),
        });
      }
    },
  });

  const handleSetSlate = (slateCode: string) =>
    setSlate({ variables: { mediaIds: [mediaId], slateCode } });

  const handleRouteMedia = (route: RouteType) =>
    routeMedia({
      variables: {
        mediaIds: [mediaId],
        routeType: route,
      },
    });

  const statusButtonDisabled =
    setSlateMutationLoading ||
    routeMutationLoading ||
    ![
      StreamState.PROGRAM_DONE,
      StreamState.PROGRAM_ON,
      StreamState.STREAM_ON,
      StreamState.STREAM_DONE,
    ].includes(pipeline.streamState);

  const isVideoPipeline = hasVideoMedia(pipeline);

  const isActiveCommercial =
    activeSlateCode === commercialSlateCode || activeSlateCode === commercialPrePostSlateCode;

  return (
    <div
      className="flex flex-col items-center gap-2"
      data-testid={`pipeline-game-state-controls-${pipeline.id}-${mediaId}`}>
      <p className="pb-1 text-sm font-bold">Game State</p>
      {debugMode ? (
        <p className="mb-1 rounded-sm border border-dashed p-1 text-sm">
          Stream State: {pipeline?.streamState}
        </p>
      ) : null}
      <StateButton
        buttonState={GAME_STATE.STAY_TUNED}
        description={`Please confirm game state change to: ${GAME_STATE.STAY_TUNED}`}
        disabled={statusButtonDisabled || isActiveCommercial}
        gameState={gameState}
        onClick={() => handleSetSlate(staticSlateCodes.stayTuned)}
      />
      {isVideoPipeline && (
        <StateButton
          buttonState={GAME_STATE.PRE_GAME}
          description={`Please confirm game state change to: ${GAME_STATE.PRE_GAME}`}
          disabled={statusButtonDisabled}
          gameState={gameState}
          onClick={() => handleRouteMedia(RouteType.PREGAME)}
        />
      )}
      <StateButton
        buttonState={GAME_STATE.GAME}
        description={`Please confirm game state change to: ${GAME_STATE.GAME}`}
        disabled={statusButtonDisabled}
        gameState={gameState}
        onClick={() => handleRouteMedia(RouteType.PROGRAM)}
      />
      {isVideoPipeline && (
        <StateButton
          buttonState={GAME_STATE.POST_GAME}
          description={`Please confirm game state change to: ${GAME_STATE.POST_GAME}`}
          disabled={statusButtonDisabled}
          gameState={gameState}
          onClick={() => handleRouteMedia(RouteType.POSTGAME)}
        />
      )}
      <StateButton
        buttonState={GAME_STATE.TYFW}
        description={`Please confirm game state change to: ${GAME_STATE.TYFW}`}
        disabled={statusButtonDisabled || isActiveCommercial}
        gameState={gameState}
        onClick={() => handleSetSlate(staticSlateCodes.thankYouForWatching)}
      />
    </div>
  );
}
